import { useState } from "react";
import { BOT_LEVEL_NAMES, BOT_LEVELS, MAX_PLAYERS } from "@grundbesitz/shared";
import type { BotLevel, RoomMember } from "@grundbesitz/shared";
import { TokenIcon } from "../board/TokenIcon";
import { Button } from "../ui/Button";
import { Sheet } from "../ui/Sheet";
import { botLabel, colorHex } from "../../lib/format";
import { useStore } from "../../lib/store";
import { colorName } from "./lobbyLogic";

type BadgeTone = "accent" | "ok" | "warn" | "muted";

function Badge({ tone, children }: { tone: BadgeTone; children: string }) {
  const tones: Record<BadgeTone, string> = {
    accent: "bg-accent text-accent-ink",
    ok: "bg-accent-soft text-ink",
    warn: "border border-warn text-warn",
    muted: "border border-line text-ink-2",
  };
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${tones[tone]}`}>{children}</span>;
}

/** The host picks the strength per bot, everybody else sees it as a badge. */
function BotLevelSelect({ bot }: { bot: RoomMember }) {
  const setBotLevel = useStore((s) => s.setBotLevel);
  return (
    <label className="flex items-center gap-1.5 text-sm text-ink-2">
      <span>Stärke</span>
      <select
        value={bot.botLevel ?? "normal"}
        onChange={(e) => void setBotLevel(bot.id, e.target.value as BotLevel)}
        aria-label={`Stärke von ${bot.name}`}
        className="min-h-11 rounded-lg border border-line bg-bg px-2 text-sm text-ink"
      >
        {BOT_LEVELS.map((l) => (
          <option key={l} value={l}>
            {BOT_LEVEL_NAMES[l]}
          </option>
        ))}
      </select>
    </label>
  );
}

function statusBadges(m: RoomMember, isHost: boolean): { tone: BadgeTone; text: string }[] {
  const out: { tone: BadgeTone; text: string }[] = [];
  if (m.isHost) out.push({ tone: "accent", text: "Host" });
  // The host sees a level picker instead.
  if (m.isBot) out.push({ tone: "muted", text: isHost ? "Bot" : botLabel(m.botLevel) });
  if (!m.connected && !m.isBot) out.push({ tone: "warn", text: m.autoPlay ? "Getrennt, Auto-Modus" : "Getrennt" });
  if (m.ready || m.isBot) out.push({ tone: "ok", text: "✓ Bereit" });
  else if (!m.isHost) out.push({ tone: "muted", text: "Nicht bereit" });
  return out;
}

export function MemberList({ members, meId, isHost }: { members: RoomMember[]; meId: string | null; isHost: boolean }) {
  const kick = useStore((s) => s.kick);
  const addBot = useStore((s) => s.addBot);
  const removeBot = useStore((s) => s.removeBot);
  const [kicking, setKicking] = useState<RoomMember | null>(null);
  const empty = Math.max(0, MAX_PLAYERS - members.length);

  return (
    <section aria-labelledby="members-title">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="members-title" className="font-display text-xl font-semibold text-ink">
          Spieler
        </h2>
        <p className="text-sm text-ink-2">
          {members.length} von {MAX_PLAYERS} Plätzen belegt
        </p>
      </div>

      <ul className="mt-3 divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface">
        {members.map((m) => {
          const isMe = m.id === meId;
          const hex = colorHex(m.color);
          return (
            <li key={m.id} className={`flex min-h-16 items-center gap-3 px-3 py-2 ${isMe ? "bg-accent-soft/50" : ""}`}>
              <span
                className="grid size-12 shrink-0 place-items-center rounded-full border-[3px] bg-surface"
                style={{ borderColor: m.color ? hex : "var(--line)" }}
              >
                {m.token ? (
                  <TokenIcon token={m.token} color={m.color ?? undefined} size={30} />
                ) : (
                  <span className="text-lg text-ink-2" aria-label="Noch keine Figur gewählt">
                    ?
                  </span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink">
                  {m.name}
                  {isMe && <span className="font-normal text-ink-2"> (Du)</span>}
                </p>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <p className="flex items-center gap-1.5 text-sm text-ink-2">
                    <span aria-hidden="true" className="size-3 shrink-0 rounded-full" style={{ background: m.color ? hex : "var(--line)" }} />
                    {colorName(m.color)}
                  </p>
                  {statusBadges(m, isHost).map((b) => (
                    <Badge key={b.text} tone={b.tone}>
                      {b.text}
                    </Badge>
                  ))}
                  {isHost && m.isBot && <BotLevelSelect bot={m} />}
                </div>
              </div>
              {isHost && !isMe && (
                <button
                  type="button"
                  className="-mr-1 grid size-11 shrink-0 place-items-center rounded-lg text-xl leading-none text-ink-2 hover:bg-surface-2 hover:text-danger"
                  aria-label={m.isBot ? `Bot ${m.name} entfernen` : `${m.name} aus dem Raum entfernen`}
                  title={m.isBot ? "Bot entfernen" : "Spieler entfernen"}
                  onClick={() => (m.isBot ? void removeBot(m.id) : setKicking(m))}
                >
                  <span aria-hidden="true">×</span>
                </button>
              )}
            </li>
          );
        })}
        {Array.from({ length: empty }, (_, i) => (
          <li key={`empty-${i}`} className="flex min-h-16 items-center gap-3 px-3 py-2 text-ink-2">
            <span className="grid size-12 shrink-0 place-items-center rounded-full border-2 border-dashed border-line" aria-hidden="true" />
            <span className="text-sm">Platz {members.length + i + 1} ist frei</span>
          </li>
        ))}
      </ul>

      {isHost && (
        <div className="mt-3">
          <Button onClick={() => void addBot()} disabled={members.length >= MAX_PLAYERS}>
            Bot hinzufügen
          </Button>
        </div>
      )}

      <Sheet open={kicking !== null} onClose={() => setKicking(null)} title={`${kicking?.name ?? "Spieler"} entfernen?`}>
        <p className="text-ink">
          {kicking?.name} wird aus dem Raum geworfen. Über den Einladungslink kann die Person wieder beitreten.
        </p>
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <Button onClick={() => setKicking(null)}>Abbrechen</Button>
          <Button
            variant="danger"
            onClick={() => {
              if (kicking) void kick(kicking.id);
              setKicking(null);
            }}
          >
            Entfernen
          </Button>
        </div>
      </Sheet>
    </section>
  );
}
